/**
 * ShareGameDialog — invite friends to play a generated game.
 *
 * Emails are entered as a comma/newline-separated list, validated client-side,
 * deduplicated, and submitted to the share-game edge function (max 10 per call).
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Send, Check, AlertCircle } from 'lucide-react';
import { gameShareService, type ShareResult } from '@/services/gameShareService';
import { useToast } from '@/hooks/use-toast';

interface ShareGameDialogProps {
  gameId: string;
  gameTitle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAILS = 10;

export const ShareGameDialog = ({
  gameId,
  gameTitle,
  open,
  onOpenChange,
}: ShareGameDialogProps) => {
  const { toast } = useToast();
  const [raw, setRaw] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<ShareResult[] | null>(null);

  const parseEmails = (): { valid: string[]; invalid: string[] } => {
    const tokens = raw
      .split(/[\s,;]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    const seen = new Set<string>();
    const valid: string[] = [];
    const invalid: string[] = [];
    for (const t of tokens) {
      const lower = t.toLowerCase();
      if (seen.has(lower)) continue;
      seen.add(lower);
      if (EMAIL_RE.test(lower)) valid.push(lower);
      else invalid.push(t);
    }
    return { valid, invalid };
  };

  const { valid, invalid } = parseEmails();
  const tooMany = valid.length > MAX_EMAILS;

  const handleSend = async () => {
    if (!valid.length || tooMany) return;
    setSubmitting(true);
    setResults(null);
    try {
      const res = await gameShareService.share(
        gameId,
        valid,
        message.trim() || undefined,
      );
      setResults(res.shares);
      toast({
        title: 'Invitations sent',
        description: `${res.sent} of ${valid.length} email${valid.length === 1 ? '' : 's'} delivered.`,
      });
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Could not share',
        description: (e as Error).message ?? 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (next: boolean) => {
    if (!next) {
      setRaw('');
      setMessage('');
      setResults(null);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share "{gameTitle}"</DialogTitle>
          <DialogDescription>
            Send a play link to friends or family. They'll need an account to play.
          </DialogDescription>
        </DialogHeader>

        {!results ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="share-emails">Email addresses</Label>
              <Textarea
                id="share-emails"
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder="friend@example.com, classmate@example.com"
                rows={3}
                disabled={submitting}
              />
              <div className="flex flex-wrap gap-1.5 text-xs">
                {valid.map((e) => (
                  <Badge key={e} variant="secondary">{e}</Badge>
                ))}
                {invalid.map((e) => (
                  <Badge key={e} variant="destructive">{e}</Badge>
                ))}
              </div>
              {tooMany && (
                <Alert variant="destructive" className="py-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Maximum {MAX_EMAILS} emails per share.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="share-message">Personal note (optional)</Label>
              <Input
                id="share-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Try this — it's fun!"
                maxLength={500}
                disabled={submitting}
              />
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => handleClose(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSend}
                disabled={!valid.length || tooMany || submitting}
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send {valid.length || ''} invite{valid.length === 1 ? '' : 's'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <ul className="space-y-1.5 text-sm">
              {results.map((r) => (
                <li key={r.email} className="flex items-center justify-between rounded border p-2">
                  <span className="truncate">{r.email}</span>
                  {r.status === 'sent' ? (
                    <Badge variant="secondary" className="gap-1">
                      <Check className="h-3 w-3" /> Sent
                    </Badge>
                  ) : (
                    <Badge variant="destructive" className="gap-1">
                      <AlertCircle className="h-3 w-3" /> Failed
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
            <DialogFooter>
              <Button onClick={() => handleClose(false)}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareGameDialog;
